import { useEffect, useState } from "react";
import api, { formatINR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Truck, Camera, Check, LogOut, MapPin, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { compressImage } from "../lib/image";

export default function MobilePOD() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [lrs, setLrs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pod, setPod] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [q, setQ] = useState("");

  async function load() {
    const { data } = await api.get("/lrs", { params: { status: "in_transit" } });
    setLrs(data);
  }
  useEffect(() => { load(); }, []);

  async function onPhoto(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { alert("Photo must be under 15 MB."); return; }
    try { setPod(await compressImage(f)); }
    catch { const reader = new FileReader(); reader.onload = () => setPod(String(reader.result)); reader.readAsDataURL(f); }
  }

  async function submit() {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/lrs/${selected.id}/pod`, { pod_image: pod, mark_delivered: true });
      setDone(true);
      setTimeout(() => {
        setSelected(null); setPod(""); setDone(false); load();
      }, 1500);
    } finally { setSaving(false); }
  }

  async function handleLogout() { await logout(); navigate("/login"); }

  const filtered = lrs.filter((lr) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (lr.lr_number || "").toLowerCase().includes(s)
        || (lr.consignee_name || "").toLowerCase().includes(s)
        || (lr.vehicle_number || "").toLowerCase().includes(s)
        || (lr.to_location || "").toLowerCase().includes(s);
  });

  if (selected) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex flex-col" data-testid="mobile-pod-detail">
        <header className="bg-[#1A1A18] text-white px-4 py-4 flex items-center gap-3 sticky top-0 z-10">
          <button onClick={() => { setSelected(null); setPod(""); setDone(false); }} className="text-white" data-testid="mobile-pod-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider opacity-70">Lorry Receipt</div>
            <div className="font-mono text-base font-bold">{selected.lr_number}</div>
          </div>
        </header>

        <div className="p-5 space-y-4 flex-1">
          <div className="card-surface p-4">
            <div className="text-xs uppercase tracking-wide text-[#68665E] mb-2">Deliver to</div>
            <div className="font-headings text-lg font-bold">{selected.consignee_name}</div>
            <div className="text-sm text-[#68665E] mt-1">{selected.consignee_address || "—"}</div>
            <div className="flex items-center gap-2 mt-3 text-sm">
              <MapPin className="w-4 h-4 text-[#C84B31]" />
              <span className="font-medium">{selected.from_location} → {selected.to_location}</span>
            </div>
          </div>

          <div className="card-surface p-4 text-sm">
            <div className="flex justify-between py-1"><span className="text-[#68665E]">Goods</span><span className="font-medium">{selected.goods_description}</span></div>
            <div className="flex justify-between py-1"><span className="text-[#68665E]">Packages</span><span className="font-medium">{selected.no_of_packages}</span></div>
            <div className="flex justify-between py-1"><span className="text-[#68665E]">Weight</span><span className="font-medium">{selected.weight_kg} kg</span></div>
            <div className="flex justify-between py-1"><span className="text-[#68665E]">Vehicle</span><span className="font-mono">{selected.vehicle_number || "—"}</span></div>
            <div className="flex justify-between py-1"><span className="text-[#68665E]">Freight</span><span className="font-medium">{formatINR(selected.freight_amount)} ({selected.payment_mode})</span></div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-[#68665E] mb-2">Proof of Delivery</div>
            {pod ? (
              <div className="relative">
                <img src={pod} alt="POD" className="w-full max-h-96 object-cover border border-[#E5E3DB]" />
                <button onClick={() => setPod("")} className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1">Retake</button>
              </div>
            ) : (
              <label className="card-surface p-8 flex flex-col items-center justify-center cursor-pointer border-dashed">
                <Camera className="w-10 h-10 text-[#C84B31] mb-3" />
                <div className="font-medium">Tap to capture</div>
                <div className="text-xs text-[#68665E] mt-1">Signed POD or delivered photo</div>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="mobile-pod-camera" />
              </label>
            )}
          </div>

          {done ? (
            <div className="bg-[#EAF2ED] text-[#2D523B] p-4 text-center" data-testid="mobile-pod-success">
              <Check className="w-6 h-6 mx-auto mb-2" />
              <div className="font-medium">Delivery marked. Thanks!</div>
            </div>
          ) : (
            <button onClick={submit} disabled={saving || !pod} className="btn-primary w-full py-4 text-base" data-testid="mobile-pod-submit">
              {saving ? "Uploading..." : "Mark Delivered"}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="mobile-pod-list">
      <header className="bg-[#1A1A18] text-white px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/ito-logo.png" alt="ITO" className="w-10 h-10 object-contain" />
            <div>
              <div className="font-headings font-bold leading-none text-sm">FIELD MODE</div>
              <div className="text-[10px] uppercase tracking-widest opacity-70 mt-0.5">{user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/app")} className="text-xs text-white/70 hover:text-white px-2" data-testid="mobile-back-to-desktop">Desktop</button>
            <button onClick={handleLogout} className="text-white/70 hover:text-white" data-testid="mobile-logout"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
        <input
          className="mt-3 w-full bg-white/10 border border-white/20 px-3 py-2 text-sm placeholder-white/50 focus:outline-none focus:border-white/50"
          placeholder="Search LR / vehicle / destination..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="mobile-search-input"
        />
      </header>

      <div className="p-4 space-y-3">
        <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">{filtered.length} in transit</div>
        {filtered.length === 0 ? (
          <div className="card-surface p-8 text-center text-[#68665E]">No active deliveries.</div>
        ) : filtered.map((lr) => (
          <button
            key={lr.id}
            onClick={() => { setSelected(lr); setPod(lr.pod_image || ""); }}
            className="card-surface p-4 w-full text-left hover:bg-[#FAFAF9] active:bg-[#F5F4F0] transition-colors"
            data-testid={`mobile-lr-card-${lr.id}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="font-mono text-xs text-[#68665E]">{lr.lr_number}</div>
              <span className="badge-pending text-[10px]">{lr.status.replace("_", " ")}</span>
            </div>
            <div className="font-headings text-lg font-bold">{lr.consignee_name}</div>
            <div className="text-sm text-[#68665E] mt-1 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" />
              {lr.from_location} → {lr.to_location}
            </div>
            <div className="flex items-center justify-between mt-3 text-sm">
              <span className="font-mono text-xs text-[#1A1A18]">{lr.vehicle_number || "—"}</span>
              <span className="font-medium">{formatINR(lr.freight_amount)}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
